/**
 * ClinicalSupplementsForm Component
 * 
 * Comprehensive form for collecting clinical supplements needed for appeal generation.
 * Combines pain assessment, functional impact, exam findings, imaging, and failed treatments.
 */

import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Stack,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

import { PainAssessmentInput } from './PainAssessmentInput';
import { FailedTreatmentsChecklist, FailedTreatment } from './FailedTreatmentsChecklist';
import { ImagingResultsForm, ImagingResult } from './ImagingResultsForm';
import { PostOpCourseForm, PostOpDay } from './PostOpCourseForm';
import { ProcedureCategory, DenialCategory } from '../../constants/appealTemplates';

export interface ClinicalSupplements {
  // Pain assessment
  painSeverity: number;
  painDescription: string;
  
  // Symptom details
  symptomOnset: string;
  symptomDuration: string;
  
  // Functional impact
  functionalImpact: string[];
  functionalImpactNotes: string;
  
  // Physical exam findings
  examFindings: string[];
  examFindingsNotes: string;
  
  // Imaging / Diagnostics
  imagingResults: ImagingResult[];
  
  // Failed treatments
  failedTreatments: FailedTreatment[];
  
  // Intraoperative findings (optional)
  intraopFindings: string;
  
  // Post-op course (conditional)
  postOpCourse: PostOpDay[];
  
  // Additional notes
  additionalNotes: string;
}

interface ClinicalSupplementsFormProps {
  value: ClinicalSupplements;
  onChange: (supplements: ClinicalSupplements) => void;
  procedureCategory: ProcedureCategory;
  denialCategory: DenialCategory;
  showPostOpCourse?: boolean;
  disabled?: boolean;
}

const FUNCTIONAL_IMPACT_OPTIONS = [
  'Difficulty standing',
  'Difficulty walking',
  'Difficulty sleeping',
  'Unable to work',
  'Limited ADLs',
  'Requires assistance',
  'Pain with sitting',
  'Numbness/tingling',
  'Weakness',
  'Balance issues',
];

const EXAM_FINDING_OPTIONS: Record<ProcedureCategory, string[]> = {
  Spine: [
    'Antalgic gait',
    'Decreased ROM',
    'Positive straight leg raise',
    'Sensory deficit',
    'Motor weakness',
    'Reflex changes',
    'Tenderness to palpation',
    'Muscle spasm',
  ],
  Nerve: [
    'Positive Tinel sign',
    'Positive Phalen test',
    'Thenar atrophy',
    'Hypothenar atrophy',
    'Decreased grip strength',
    'Sensory deficit',
    'Ulnar claw deformity',
    'Decreased 2-point discrimination',
  ],
  Arthroscopy: [
    'Joint effusion',
    'Decreased ROM',
    'Positive McMurray test',
    'Joint line tenderness',
    'Instability',
    'Crepitus',
    'Positive apprehension test',
  ],
  General: [
    'Tenderness',
    'Swelling',
    'Decreased ROM',
    'Weakness',
    'Sensory changes',
  ],
};

const getCompletionScore = (supplements: ClinicalSupplements): { score: number; total: number; missing: string[] } => {
  const missing: string[] = [];
  let score = 0;
  const total = 6;

  if (supplements.painSeverity > 0 || supplements.painDescription) score++;
  else missing.push('Pain Assessment');

  if (supplements.functionalImpact.length > 0 || supplements.functionalImpactNotes) score++;
  else missing.push('Functional Impact');

  if (supplements.examFindings.length > 0 || supplements.examFindingsNotes) score++;
  else missing.push('Exam Findings');

  if (supplements.imagingResults.length > 0) score++;
  else missing.push('Imaging Studies');

  if (supplements.failedTreatments.some(t => t.selected)) score++;
  else missing.push('Failed Treatments');

  if (supplements.symptomOnset || supplements.symptomDuration) score++;
  else missing.push('Symptom Timeline');

  return { score, total, missing };
};

export const ClinicalSupplementsForm: React.FC<ClinicalSupplementsFormProps> = ({
  value,
  onChange,
  procedureCategory,
  denialCategory,
  showPostOpCourse = false,
  disabled = false,
}) => {
  const updateField = <K extends keyof ClinicalSupplements>(
    field: K,
    fieldValue: ClinicalSupplements[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const toggleFunctionalImpact = (impact: string) => {
    const current = value.functionalImpact;
    const updated = current.includes(impact)
      ? current.filter(i => i !== impact)
      : [...current, impact];
    updateField('functionalImpact', updated);
  };

  const toggleExamFinding = (finding: string) => {
    const current = value.examFindings;
    const updated = current.includes(finding)
      ? current.filter(f => f !== finding)
      : [...current, finding];
    updateField('examFindings', updated);
  };

  const completion = getCompletionScore(value);
  const completionPercent = Math.round((completion.score / completion.total) * 100);

  return (
    <Box>
      {/* Completion indicator */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: completionPercent === 100 ? '#f0fdf4' : '#fffbeb',
          border: `1px solid ${completionPercent === 100 ? '#22c55e' : '#f59e0b'}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          {completionPercent === 100 ? (
            <CheckIcon color="success" />
          ) : (
            <WarningIcon color="warning" />
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2">
              Clinical Documentation: {completionPercent}% Complete
            </Typography>
            {completion.missing.length > 0 && (
              <Typography variant="caption" color="textSecondary">
                Missing: {completion.missing.join(', ')}
              </Typography>
            )}
          </Box>
          <Chip
            label={`${completion.score}/${completion.total}`}
            size="small"
            color={completionPercent === 100 ? 'success' : 'warning'}
          />
        </Stack>
      </Paper>

      <Stack spacing={3}>
        {/* Pain Assessment */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Pain Assessment
              </Typography>
              {(value.painSeverity > 0 || value.painDescription) && (
                <CheckIcon fontSize="small" color="success" />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <PainAssessmentInput
              painSeverity={value.painSeverity}
              painDescription={value.painDescription}
              onPainSeverityChange={(v) => updateField('painSeverity', v)}
              onPainDescriptionChange={(v) => updateField('painDescription', v)}
              disabled={disabled}
            />
          </AccordionDetails>
        </Accordion>

        {/* Symptom Timeline */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Symptom Timeline
              </Typography>
              {(value.symptomOnset || value.symptomDuration) && (
                <CheckIcon fontSize="small" color="success" />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Symptom Onset"
                placeholder="e.g., December 2024, after a fall"
                value={value.symptomOnset}
                onChange={(e) => updateField('symptomOnset', e.target.value)}
                disabled={disabled}
                size="small"
              />
              <TextField
                fullWidth
                label="Duration"
                placeholder="e.g., 6 months, progressive"
                value={value.symptomDuration}
                onChange={(e) => updateField('symptomDuration', e.target.value)}
                disabled={disabled}
                size="small"
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Functional Impact */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Functional Impact
              </Typography>
              <Tooltip title="Document how symptoms affect daily activities. This strengthens medical necessity arguments.">
                <InfoIcon fontSize="small" color="action" />
              </Tooltip>
              {value.functionalImpact.length > 0 && (
                <Chip label={value.functionalImpact.length} size="small" color="primary" />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 2 }}>
              {FUNCTIONAL_IMPACT_OPTIONS.map((impact) => (
                <Chip
                  key={impact}
                  label={impact}
                  size="small"
                  variant={value.functionalImpact.includes(impact) ? 'filled' : 'outlined'}
                  color={value.functionalImpact.includes(impact) ? 'primary' : 'default'}
                  onClick={() => toggleFunctionalImpact(impact)}
                  disabled={disabled}
                  sx={{ mr: 0.5, mb: 0.5, cursor: 'pointer' }}
                />
              ))}
            </Box>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Additional Notes"
              placeholder="Describe specific functional limitations..."
              value={value.functionalImpactNotes}
              onChange={(e) => updateField('functionalImpactNotes', e.target.value)}
              disabled={disabled}
              size="small"
            />
          </AccordionDetails>
        </Accordion>

        {/* Physical Exam Findings */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Physical Examination Findings
              </Typography>
              {value.examFindings.length > 0 && (
                <Chip label={value.examFindings.length} size="small" color="primary" />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 2 }}>
              {EXAM_FINDING_OPTIONS[procedureCategory].map((finding) => (
                <Chip
                  key={finding}
                  label={finding}
                  size="small"
                  variant={value.examFindings.includes(finding) ? 'filled' : 'outlined'}
                  color={value.examFindings.includes(finding) ? 'primary' : 'default'}
                  onClick={() => toggleExamFinding(finding)}
                  disabled={disabled}
                  sx={{ mr: 0.5, mb: 0.5, cursor: 'pointer' }}
                />
              ))}
            </Box>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Additional Findings"
              placeholder="Document additional exam findings..."
              value={value.examFindingsNotes}
              onChange={(e) => updateField('examFindingsNotes', e.target.value)}
              disabled={disabled}
              size="small"
            />
          </AccordionDetails>
        </Accordion>

        {/* Imaging Results */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Imaging / Diagnostic Studies
              </Typography>
              {value.imagingResults.length > 0 && (
                <Chip label={value.imagingResults.length} size="small" color="primary" />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <ImagingResultsForm
              value={value.imagingResults}
              onChange={(v) => updateField('imagingResults', v)}
              disabled={disabled}
              procedureCategory={procedureCategory}
            />
          </AccordionDetails>
        </Accordion>

        {/* Failed Treatments */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Failed Conservative Management
              </Typography>
              {value.failedTreatments.filter(t => t.selected).length > 0 && (
                <Chip 
                  label={value.failedTreatments.filter(t => t.selected).length} 
                  size="small" 
                  color="primary" 
                />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <FailedTreatmentsChecklist
              value={value.failedTreatments}
              onChange={(v) => updateField('failedTreatments', v)}
              disabled={disabled}
            />
          </AccordionDetails>
        </Accordion>

        {/* Intraoperative Findings */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Intraoperative Findings
              </Typography>
              <Typography variant="caption" color="textSecondary">
                (if applicable)
              </Typography>
              {value.intraopFindings && (
                <CheckIcon fontSize="small" color="success" />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Document key intraoperative findings that justified the surgical approach (e.g., ulnar nerve subluxation, severe stenosis visualized)..."
              value={value.intraopFindings}
              onChange={(e) => updateField('intraopFindings', e.target.value)}
              disabled={disabled}
              size="small"
            />
          </AccordionDetails>
        </Accordion>

        {/* Post-Op Course (conditional) */}
        {showPostOpCourse && (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Post-Operative Course
                </Typography>
                <Chip label="Required" size="small" color="error" variant="outlined" />
                {value.postOpCourse.length > 0 && (
                  <Chip label={`${value.postOpCourse.length} days`} size="small" color="primary" />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <PostOpCourseForm
                value={value.postOpCourse}
                onChange={(v) => updateField('postOpCourse', v)}
                disabled={disabled}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Additional Notes */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Additional Notes
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Any additional clinical information or context for the AI to consider..."
              value={value.additionalNotes}
              onChange={(e) => updateField('additionalNotes', e.target.value)}
              disabled={disabled}
              size="small"
            />
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Box>
  );
};

export const getDefaultClinicalSupplements = (): ClinicalSupplements => ({
  painSeverity: 0,
  painDescription: '',
  symptomOnset: '',
  symptomDuration: '',
  functionalImpact: [],
  functionalImpactNotes: '',
  examFindings: [],
  examFindingsNotes: '',
  imagingResults: [],
  failedTreatments: [],
  intraopFindings: '',
  postOpCourse: [],
  additionalNotes: '',
});
